import { Component, OnInit, ChangeDetectorRef, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
//import { RouterLink } from '@angular/router';
import { AuthService } from '../../Service/auth';
import { UdcsService } from '../../Service/udcs-service'; // 🚀 Inyectado

export interface UserPreferences {
  mostrarMetricas: boolean;
  mostrarAvisos: boolean;
  mostrarDirectorio: boolean;
}

@Component({
  selector: 'app-home',
  standalone: true,  
  imports: [CommonModule, FormsModule],
  templateUrl: './home.html',
  styleUrl: './home.css',
})
export class HomeComponent implements OnInit {
  usuarioFirmado: string = 'Invitado';
  avisosDinamicos: any[] = [];
  directorio: any[] = [];

  // =========================================================================
  // 📈 SUITE DE METRICAS OPERATIVAS Y FINANCIERAS MULTI-TENANT (PREPARADAS)
  // Agrupamos los KPIs en objetos lógicos para facilitar su renderizado condicional.
  // =========================================================================
  kpis = {
    // 1. Bloque Técnico / Operativo (Visible para Mecánicos, Jefes y Admin)
    vehiculosProceso: 14,
    ordenesHoy: 6,
    eficienciaBahia: '92%',       // Horas reales vs asignadas
    cycleTimePromedio: '2.4 Días', // Estancia total del auto
    retrabajosTasa: '1.2%',        // Garantías/Retornos
    onTimeDelivery: '95.8%',       // Promesas cumplidas

    // 2. Bloque Comercial (Visible para Jefes de Taller y Admin)
    clientesNuevos: 8,
    conversionPresupuestos: '74%', // Cotizaciones aceptadas
    tiempoValuacionAseguradora: '4.2 Hrs',

    // 3. Bloque Financiero Estricto (Exclusivo de ROLE_ADMIN)
    totalCotizado: '$124,500.00',
    ticketPromedio: '$8,450.00',
    margenUtilidadOrden: '32.5%',
    antiguedadSaldos30Dias: '$45,000.00'
  };

  // Tu cojinete de permisos que lee el rol desde el AuthService
  esSuperAdminODueno: boolean = false;
  esJefeTaller: boolean = false;
    
  // 🎛️ COJINETE DE PREFERENCIAS REACTIVAS
  pref!: UserPreferences;

  constructor(
    private authService: AuthService,
    private udcService: UdcsService, // 🚀 Inyección Máster
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    // 👤 Recuperamos el Login ID real purificado por tu servicio
    this.usuarioFirmado = this.authService.getUserId().toUpperCase();
    this.inicializarPreferencias();
    this.cargarTablonAvisosUDC();
    this.cargarDirectorioInterno();
  }

  /**
   * 🔒 PERSISTENCIA EN NAVEGADOR:
   * Inicializa o lee la configuración del usuario desde el LocalStorage seguro contra SSR
   */
  inicializarPreferencias(): void {
    const defaultPrefs: UserPreferences = {
      mostrarMetricas: true,
      mostrarAvisos: true,
      mostrarDirectorio: true
    };

    if (isPlatformBrowser(this.platformId)) {
      const saved = localStorage.getItem(`prefs_${this.usuarioFirmado}`);
      this.pref = saved ? JSON.parse(saved) : defaultPrefs;
    } else {
      this.pref = defaultPrefs;
    }
  }

  /**
   * 💾 ESCUCHADOR DE CAMBIO EN SWITCHES:
   * Salva en caliente la personalización cada vez que el usuario mueve un interruptor
   */
  guardarPreferencias(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(`prefs_${this.usuarioFirmado}`, JSON.stringify(this.pref));
      console.log('>>> [PREFS JDE] Preferencias guardadas para:', this.usuarioFirmado, this.pref);
    }
  }


  /**
   * 🔄 RENDERIZADO EN CALIENTE (CERO HARDCODEO):
   * Mantiene tu lógica original descargando el periódico mural.
   */
  cargarTablonAvisosUDC(): void {
    console.log('>>> [PORTAL JDE] Descargando periódico mural dinámico desde la UDC...');
    
    this.udcService.getCatBySystemCode('PORTAL').subscribe({
      next: (data: any[]) => {
        // En tu backend, este endpoint ya filtra por system = 'PORTAL'.
        // Para que solo pinte los avisos, filtramos los que tengan la clave 'AVISOS'
        const registrosAvisos = data.filter(item => item.udcKey === 'AVISOS');

        this.avisosDinamicos = registrosAvisos.map(item => ({
          id: item.id,
          tipo: item.strValue1 ? item.strValue1.toLowerCase() : 'info', 
          titulo: item.strValue2 || 'COMUNICADO OFICIAL',
          contenido: item.description || 'Sin contenido registrado.',
          fecha: 'Vigente'
        }));

        console.log('>>> [PORTAL JDE] Tablón poblado en vivo. Avisos activos:', this.avisosDinamicos.length);
        this.cdr.detectChanges();
      },
      error: (err) => console.error('Error al descargar avisos desde UDC:', err)
    });
  }

  /**
   * 🔄 REFACTORIZADO POR ADUANA DE RESPONSABILIDADES:
   * 💡 Solución al error ts(2339). Usamos tu método real getCatBySystemCode('PORTAL')
   * y extraemos con un .filter() en memoria únicamente los registros con udckey = 'DIR'.
   */
  cargarDirectorioInterno(): void {
    console.log('>>> [HOME] Descargando directorio telefónico dinámico Multi-Tenant...');
    
    // Consumimos el método real de tu servicio para el sistema 'PORTAL'
    this.udcService.getCatBySystemCode('PORTAL').subscribe({
      next: (data: any[]) => {
        
        // 💡 AISLAMIENTO EN MEMORIA: Filtramos de forma exclusiva los del Directorio
        const registrosDirectorio = data.filter(item => item.udcKey === 'DIR');

        // Mapeamos de forma limpia respetando tus variables de pantalla originales
        this.directorio = registrosDirectorio.map(udc => ({
          area: udc.note || udc.strValue1, // 'Recepción y Asesores'
          responsable: udc.description,    // 'Operador Comercial Asignado' (Texto Institucional)
          extension: udc.strValue2         // '101'
        }));

        console.log('>>> [HOME] Directorio poblado en vivo. Extensiones activas:', this.directorio.length);
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Fallo al recuperar el directorio de la UDC:', err);
      }
    });
  }

}
